/**
 * The weekly honors sweep: player of the week, Mackey, anything an athlete got
 * that week. The job (scraper-api/jobs/honorSweep) reads the news and the
 * conferences; this holds the rules, and is what the page and Claude settle with.
 *
 * NOTHING GOES ON A RECORD UNTIL A PERSON SAYS SO. These are headlines, and a
 * headline can be about another player with the same name, so each week's finds
 * for an athlete wait in Needs you with their sources and links. "Add to record"
 * writes what they said to clients.awards; "Not an honor" drops them.
 *
 * WHO SAID IT. A league, conference, team or school saying it is official; any
 * other outlet is labelled as reporting it. A fan site's "Player of the Game" is
 * not the same as a conference's, and the person deciding should see which.
 *
 * Pure apart from settleHonor, which takes whichever PocketBase client is asking.
 */

export interface HonorHeadline {
  title: string
  source: string
  url: string
  official: boolean
  /** YYYY-MM-DD */
  date: string
}

export interface HonorFind {
  id: string
  client_id: string
  /** The Monday of the sweep's week, YYYY-MM-DD. */
  week: string
  headlines: HonorHeadline[]
}

/**
 * Conferences whose football feed the sweep reads, and where. The SEC is absent
 * because secsports.com has no feed at this address (checked Nov 3 2026); its
 * athletes are still covered by the news search.
 */
export const CONFERENCE_FEEDS: { name: string; host: string }[] = [
  { name: 'Big Ten', host: 'bigten.org' },
  { name: 'ACC', host: 'theacc.com' },
  { name: 'Big 12', host: 'big12sports.com' },
  { name: 'Mountain West', host: 'themw.com' },
  { name: 'American', host: 'theamerican.org' },
  { name: 'Sun Belt', host: 'sunbeltsports.org' },
  { name: 'MAC', host: 'getsomemaction.com' },
  { name: 'Conference USA', host: 'conferenceusa.com' },
  { name: 'Pac-12', host: 'pac-12.com' },
  { name: 'Ivy League', host: 'ivyleague.com' },
  { name: 'CAA', host: 'caasports.com' },
  { name: 'Missouri Valley', host: 'valley-football.org' },
  { name: 'Big Sky', host: 'bigskyconf.com' },
]

const OFFICIAL_HOSTS = new Set(['nfl.com', 'ncaa.com', 'ufl.com', ...CONFERENCE_FEEDS.map(c => c.host)])

const HONOR = /(of the week|of the game|player of the|of the year|award|trophy|watch list|semifinalist|finalist|all-american|all-america\b|all-conference|all-pro|pro bowl|honou?rs?\b|honou?red|game ball|team of the week)/i
/**
 * About an honor without being one. Each word here comes from a real headline in
 * the first sweep or the test before it ("Heisman Trophy slipping away?", "early
 * catch of the year contender"). A question is never an announcement.
 */
const NOT_YET = /\?|\b(could|should|might|odds|predict\w*|mock|tracker|candidates?|candidacy|contenders?|slipping|snub\w*|vote for|rankings?|race)\b/i
/** Somebody actually got it: a word that says so, or a kind of honor that is its own announcement. */
const GOT_IT = /\b(named|names|earns?|earned|wins?|won|selected|receives?|received|honou?red|tabbed|voted|awarded|garners?|claims?|takes home)\b|of the week|of the game|semifinalist|finalist|watch list|all-america|all-conference|all-pro|pro bowl/i
const SUFFIX = /^(jr\.?|sr\.?|ii|iii|iv|v)$/i

const decode = (s: string) => s
  .replace(/<!\[CDATA\[|\]\]>/g, '')
  .replace(/&amp;/g, '&').replace(/&#0?39;|&apos;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ')
  .trim()

const tag = (xml: string, name: string) => {
  const m = new RegExp(`<${name}[^>]*>([\\s\\S]*?)</${name}>`).exec(xml)
  return m ? decode(m[1]) : ''
}

const isoDay = (value: string): string | null => {
  const t = Date.parse(value)
  return Number.isFinite(t) ? new Date(t).toISOString().slice(0, 10) : null
}

export interface NewsItem { title: string; source: string; host: string; url: string; date: string }

/** A news-search feed. An item with no readable date is dropped: "this week" cannot be claimed of it. */
export function parseNewsItems(xml: string): NewsItem[] {
  const out: NewsItem[] = []
  for (const [item] of xml.matchAll(/<item>[\s\S]*?<\/item>/g)) {
    const date = isoDay(tag(item, 'pubDate'))
    const source = tag(item, 'source')
    const host = /<source url="([^"]*)"/.exec(item)?.[1] ?? ''
    if (!date) continue
    // The search appends " - Outlet" to every headline.
    const title = tag(item, 'title').replace(new RegExp(`\\s+-\\s+${source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`), '')
    out.push({ title, source, host: host.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, ''), url: tag(item, 'link'), date })
  }
  return out
}

/** A conference's own feed: title, link and date of each release. */
export function parseFeedItems(xml: string): { title: string; url: string; date: string }[] {
  const out: { title: string; url: string; date: string }[] = []
  for (const [item] of xml.matchAll(/<item>[\s\S]*?<\/item>/g)) {
    const date = isoDay(tag(item, 'pubDate'))
    if (date) out.push({ title: tag(item, 'title'), url: tag(item, 'link'), date })
  }
  return out
}

export const isHonorsRelease = (title: string) => /of the week|weekly .*honors?|honors? awarded/i.test(title)

/**
 * Whether it was published in the sweep's window. Checked here because the news
 * search ignores its own date limit on some queries: a test returned 2017.
 */
export function isThisWeek(date: string, now: Date, days = 8): boolean {
  const age = (now.getTime() - Date.parse(`${date}T00:00:00Z`)) / 86_400_000
  return age >= -1 && age <= days
}

export function lastNameOf(fullName: string): string {
  const words = fullName.trim().split(/\s+/).filter(w => !SUFFIX.test(w))
  return words[words.length - 1] ?? ''
}

/** A headline that names the athlete and says an honor happened. */
export function looksLikeHonor(title: string, fullName: string): boolean {
  const last = lastNameOf(fullName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  if (!last) return false
  return HONOR.test(title) && GOT_IT.test(title) && !NOT_YET.test(title) && new RegExp(`\\b${last}\\b`, 'i').test(title)
}

/** A league, conference, the athlete's team, or a school's athletics department. */
export function isOfficialSource(source: string, host: string, teamName: string | null | undefined): boolean {
  if (/\bathletics\b|official site/i.test(source)) return true
  if (OFFICIAL_HOSTS.has(host)) return true
  const team = String(teamName ?? '').trim().toLowerCase()
  if (!team) return false
  if (source.trim().toLowerCase() === team) return true
  const words = team.split(/\s+/)
  return host === `${team.replace(/[^a-z0-9]/g, '')}.com` || host === `${words[words.length - 1]}.com`
}

export function textOfHtml(html: string): string {
  return decode(html.replace(/<script[\s\S]*?<\/script>|<style[\s\S]*?<\/style>/gi, ' ').replace(/<[^>]+>/g, ' ')).replace(/\s+/g, ' ')
}

/** "Iowa Hawkeyes" → "Iowa": releases name the school, not the mascot. */
const schoolOf = (team: string) => {
  const words = team.trim().split(/\s+/)
  return words.length > 1 ? words.slice(0, -1).join(' ') : team.trim()
}

/**
 * The honor a conference release gives this athlete, or null. Needs the full
 * name with the school right after it ("Hank Brown, Iowa QB"), so a player of
 * the same name elsewhere in the conference is not taken for them. An athlete
 * whose record has no team is not matched.
 */
export function honorInRelease(text: string, fullName: string, team: string | null | undefined): string | null {
  const school = team ? schoolOf(team) : ''
  if (!fullName.trim() || !school) return null
  for (const m of text.matchAll(new RegExp(fullName.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'))) {
    const at = m.index ?? 0
    if (!text.slice(at, at + fullName.length + 40).toLowerCase().includes(school.toLowerCase())) continue
    const before = text.slice(Math.max(0, at - 90), at)
    const honors = [...before.matchAll(/((?:[A-Z][\w'’-]*\s+){1,4}of the Week)/g)]
    return honors.length ? honors[honors.length - 1][1] : 'Honored'
  }
  return null
}

const titleKey = (title: string) => title.toLowerCase().replace(/[^a-z0-9]/g, '')

/** Adds what is new, official sources first. */
export function mergeHeadlines(existing: HonorHeadline[], incoming: HonorHeadline[]): HonorHeadline[] {
  const seen = new Set(existing.map(h => titleKey(h.title)))
  const out = [...existing]
  for (const h of incoming) {
    if (seen.has(titleKey(h.title))) continue
    seen.add(titleKey(h.title))
    out.push(h)
  }
  return out.sort((a, b) => Number(b.official) - Number(a.official))
}

/** Headlines nobody has already been asked about in an earlier week. */
export function notSeenBefore(headlines: HonorHeadline[], earlier: HonorHeadline[]): HonorHeadline[] {
  const seen = new Set(earlier.map(h => titleKey(h.title)))
  return headlines.filter(h => !seen.has(titleKey(h.title)))
}

/** The Monday of this date's week (UTC). */
export function sweepWeekOf(now: Date): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7))
  return d.toISOString().slice(0, 10)
}

/**
 * From Wednesday morning, US time: conferences publish Monday, national weekly
 * honors Tuesday, NFL players of the week Wednesday. Later in the week still
 * counts, so a service that was down on Wednesday catches up.
 */
export function sweepDue(now: Date): boolean {
  const day = now.getUTCDay()
  return (day === 3 && now.getUTCHours() >= 14) || day > 3 || day === 0
}

/** A big week drew nine headlines in testing. A few say it; official sources come first. */
const SHOWN = 4
const officialFirst = (headlines: HonorHeadline[]) => [...headlines].sort((a, b) => Number(b.official) - Number(a.official))
const andMore = (headlines: HonorHeadline[]) => {
  const rest = headlines.length - SHOWN
  return rest > 0 ? `and ${rest} more report${rest === 1 ? '' : 's'}` : null
}

/** The line that goes on the record. */
export function awardsLine(headlines: HonorHeadline[]): string {
  const newest = headlines.map(h => h.date).sort().pop() ?? ''
  const said = officialFirst(headlines).slice(0, SHOWN).map(h => `${h.title} (${h.source}${h.official ? '' : ', unconfirmed'})`)
  return `${newest}: ${[...said, andMore(headlines)].filter(Boolean).join('; ')}`
}

export function withAward(current: string | null | undefined, line: string): string {
  const had = String(current ?? '').trim()
  return had ? `${had}\n${line}` : line
}

/** How a find is said in Needs you. */
export function honorDetail(headlines: HonorHeadline[]): string {
  const said = officialFirst(headlines).slice(0, SHOWN).map(h => `${h.official ? 'Official' : 'Reported, not official'}: "${h.title}" (${h.source}, ${h.date})`)
  return [...said, andMore(headlines)].filter(Boolean).join(' · ')
}

/** Where to read it, for the same headlines the detail names. */
export function honorLinks(headlines: HonorHeadline[]): { label: string; url: string }[] {
  return officialFirst(headlines).slice(0, SHOWN).map(h => ({ label: h.source, url: h.url }))
}

/** A failed source in the latest sweep, in words, so an empty list cannot read as "no honors". */
export function sweepProblems(sweep: Record<string, any> | null | undefined): string[] {
  if (!sweep || !Array.isArray(sweep.problems)) return []
  return sweep.problems.map(p => `Honors sweep, week of ${sweep.week}: ${String(p)}`)
}

type Pb = { collection: (name: string) => { getOne: (id: string, opts?: any) => Promise<any>; update: (id: string, body: any) => Promise<any> } }

/** A person's answer. The record is written first: if that fails, the find stays waiting. */
export async function settleHonor(pb: Pb, findId: string, decision: 'add' | 'dismiss', userId: string): Promise<string> {
  const find = await pb.collection('honor_finds').getOne(findId, { fields: 'id,client_id,headlines,status' })
  if (find.status !== 'new') throw new Error('Someone has already answered this one.')
  if (decision === 'add') {
    const client = await pb.collection('clients').getOne(find.client_id, { fields: 'id,awards' })
    await pb.collection('clients').update(find.client_id, { awards: withAward(client.awards, awardsLine(find.headlines)), updated_by: userId })
  }
  await pb.collection('honor_finds').update(findId, {
    status: decision === 'add' ? 'added' : 'dismissed',
    decided_by: userId,
    decided_at: new Date().toISOString().replace('T', ' '),
  })
  return decision === 'add' ? 'Added to their awards, with the sources.' : 'Dropped. It will not be asked again.'
}
